import React from 'react';

/**
 * React.lazy that survives a deploy. Chunks are content-hashed, so a tab
 * opened before a Cloudflare Pages deploy asks for a chunk that no longer
 * exists (or isn't propagated yet) and the browser throws "Failed to fetch
 * dynamically imported module" / the text/html MIME error. On that failure
 * reload the page to pick up the fresh index.html.
 *
 * The guard is a timestamp rather than a flag: a flag set by one deploy
 * used to stay set for the rest of the tab's session, so the next deploy's
 * stale chunk threw straight away with no reload. A reload is skipped only
 * if one already happened in the last RELOAD_WINDOW_MS, which still stops a
 * genuinely broken build from looping.
 */
export const RELOAD_GUARD = 'tifpa:chunk-reload';
const RELOAD_WINDOW_MS = 60_000;

/** True if a reload is allowed now; records the attempt when it is. */
export function claimChunkReload(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_GUARD));
    if (last && now - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_GUARD, String(now));
    return true;
  } catch {
    // Storage unavailable: no way to tell a loop apart, so don't reload
    return false;
  }
}

// Marks errors that came out of a lazyWithReload import, whatever the
// browser's wording
const CHUNK_ERROR = Symbol.for('tifpa:chunk-load-error');

export function isChunkLoadError(err: unknown): boolean {
  if (err && typeof err === 'object' && CHUNK_ERROR in err) return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(
    msg
  );
}

export function lazyWithReload<T extends React.ComponentType<unknown>>(
  load: () => Promise<{ default: T }>
) {
  return React.lazy(() =>
    load().catch((err: unknown) => {
      if (claimChunkReload()) {
        window.location.reload();
        // Never resolves — the reload tears the page down
        return new Promise<{ default: T }>(() => {});
      }
      if (err && typeof err === 'object') {
        Object.defineProperty(err, CHUNK_ERROR, { value: true });
      }
      throw err;
    })
  );
}

interface BoundaryState {
  error: unknown;
}

/**
 * Catches a chunk that still won't load after the automatic reload and
 * offers a manual reload instead of unmounting the whole app to a blank
 * page. Any other error is rethrown unchanged.
 */
export class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode },
  BoundaryState
> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (error === null) return this.props.children;
    if (!isChunkLoadError(error)) throw error;
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
        <p className="text-sm text-muted-foreground">
          The portal was updated. Reload to get the latest version.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow hover:bg-primary/90"
        >
          Reload
        </button>
      </div>
    );
  }
}
